import {
  KatanaPerpsOrder,
  KatanaPerpsOrderEventDataGeneral,
} from '@katanaperps/katana-perps-sdk';
import {
  QueryClient,
  skipToken,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import { fetchAllUserActiveOrders, fetchOrderDetail } from 'api/apiExchange';
import { includeBaseAndQuoteFromMarket, isOrderInactive } from 'api/apiUtils';
import { ApiOrder } from 'api/types';
import {
  useActiveWindowInterval,
  useWebsocketEventListener,
} from 'components/hooks';
import { updateMinedOrderFillInArray } from 'components/trading/MyTrades/fillsFetcherHook';
import { debouncerByKey } from 'lib/throttle';
import { useSelector } from 'reduxState/store';

import { minutes, seconds } from './utils';

export const useOrdersQueriesInvalidator = () => {
  const queryClient = useQueryClient();
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const queryState = queryClient.getQueryState(['orders', walletAddress]);

  return {
    addOrUpdateOrderCachedQuery: (
      newOrderRaw: KatanaPerpsOrder,
      /** Process inactive orders from WebSocket, Exclude from the API response (eg. market orders) */
      processInactive: boolean
    ) => {
      if (
        processInactive === false &&
        isOrderInactive(newOrderRaw.status, newOrderRaw.type)
      ) {
        console.debug('[orderQueries] New order is inactive (ignoring)');
        return;
      }

      // TODO: we are not removing for initial fetch, we probably shouldn't here ?
      // We dont need to store list of fills here
      // delete newOrder.fills;

      const newOrder: Omit<ApiOrder, 'fills'> = includeBaseAndQuoteFromMarket({
        ...newOrderRaw,
        fills: undefined,
      });
      queryClient.setQueryData<ApiOrder[]>(
        ['orders', walletAddress],
        previousOrders => {
          if (!previousOrders) {
            return undefined; // README P9-4765
          }
          const exists = previousOrders.some(
            order => order.orderId === newOrder.orderId
          );
          return exists
            ? previousOrders.map(order =>
                order.orderId === newOrder.orderId ? newOrder : order
              )
            : [newOrder, ...previousOrders];
        },
        {
          updatedAt: queryState?.dataUpdatedAt || Date.now(), // preserves stale status
        }
      );
    },
    removeOrderFromCachedQuery: (orderId: string) => {
      queryClient.setQueryData<ApiOrder[]>(
        ['orders', walletAddress],
        previousOrders => {
          if (!previousOrders) {
            return undefined; // README P9-4765
          }
          const exists = previousOrders.some(
            order => order.orderId === orderId
          );
          return exists
            ? previousOrders.filter(order => orderId !== order.orderId)
            : previousOrders;
        },
        {
          updatedAt: queryState?.dataUpdatedAt || Date.now(), // preserves stale status
        }
      );
    },
  };
};

const updateConditionalTpslInOrdersQuery = (
  queryClient: QueryClient,
  walletAddress: string,
  conditionalOrderRaw: KatanaPerpsOrderEventDataGeneral
) => {
  if (!conditionalOrderRaw.conditionalOrderId) {
    throw new Error('Order is missing conditionalOrderId');
  }
  const conditionalOrder: Omit<ApiOrder, 'fills'> =
    includeBaseAndQuoteFromMarket({
      ...conditionalOrderRaw,
      fills: undefined,
    });
  queryClient.setQueryData<ApiOrder[]>(
    ['orders', walletAddress],
    previousOrders => {
      if (!previousOrders) {
        return undefined; // README P9-4765
      }
      const existingOrder = previousOrders.find(
        order => order.orderId === conditionalOrder.conditionalOrderId
      );
      if (
        conditionalOrder.status === 'canceled' &&
        conditionalOrder.type === 'takeProfitMarket' &&
        existingOrder?.conditionalTakeProfitOrder?.orderId !==
          conditionalOrder.orderId
      ) {
        // When replacing: Conditional cancel event received after parent order cancel
        console.log('Ignoring removal of non-existing conditional TL order');
        return undefined;
      }
      if (
        conditionalOrder.status === 'canceled' &&
        conditionalOrder.type === 'stopLossMarket' &&
        existingOrder?.conditionalStopLossOrder?.orderId !==
          conditionalOrder.orderId
      ) {
        // When replacing: Conditional cancel event received after parent order cancel
        console.log('Ignoring removal of non-existing conditional SL order');
        return undefined; // Already replaced order - Ignoring
      }
      return previousOrders.map(order => {
        if (order.orderId === conditionalOrder.conditionalOrderId) {
          // TODO: type guard ?
          const conditionalTake = conditionalOrder as ApiOrder & {
            type: 'takeProfitMarket';
          };
          const conditionalStop = conditionalOrder as ApiOrder & {
            type: 'stopLossMarket';
          };
          const updated: ApiOrder = {
            ...order,
            conditionalStopLossOrder:
              conditionalOrder.type === 'stopLossMarket'
                ? conditionalOrder.status === 'canceled'
                  ? undefined // Removed
                  : conditionalStop // Updated
                : order.conditionalStopLossOrder, // Unchanged
            conditionalTakeProfitOrder:
              conditionalOrder.type === 'takeProfitMarket'
                ? conditionalOrder.status === 'canceled'
                  ? undefined // Removed
                  : conditionalTake // Updated
                : order.conditionalTakeProfitOrder, // Unchanged
          };
          return updated;
        }
        return order;
      });
    }
  );
};

/**
 * Running this globally allow us to have long stale time on order
 * queries which are not always active. */
export const useGlobalOrdersQueriesInvalidator = () => {
  const queryClient = useQueryClient();

  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const websocketConnected = useSelector(
    state => state.exchange.websocketState.connected
  );

  const { removeOrderFromCachedQuery, addOrUpdateOrderCachedQuery } =
    useOrdersQueriesInvalidator();

  const invalidateOrders = useCallback(
    () =>
      queryClient.invalidateQueries({ queryKey: ['orders', walletAddress] }),
    [queryClient, walletAddress]
  );

  // Refetch periodically
  useActiveWindowInterval(() => {
    invalidateOrders();
  }, 60_000);

  // Refetch after websocket reconnect
  const previousConnectedRef = useRef(websocketConnected);
  useEffect(() => {
    if (
      previousConnectedRef.current !== undefined && // Is not first run
      !previousConnectedRef.current && // And is false -> true
      websocketConnected
    ) {
      console.log(
        '[useGlobalOrdersQueriesInvalidator] Refetching orders after WS reconnect'
      );
      invalidateOrders();
    }
    previousConnectedRef.current = websocketConnected;
  }, [websocketConnected, invalidateOrders, queryClient, walletAddress]);

  // Update on WS events
  useWebsocketEventListener('orders', event => {
    if (!event.data.type) {
      // Invalidate all markets for Deleverage/Liquidations/Closures
      invalidateOrders();
      return;
    }

    if (event.data.conditionalOrderId) {
      // Update conditional TPSL orders (created, replaced, canceled)
      updateConditionalTpslInOrdersQuery(
        queryClient,
        walletAddress,
        event.data
      );
      return;
    }

    const orderIsInactive = isOrderInactive(event.data.status, event.data.type);
    if (orderIsInactive) {
      // Remove existing inactive order (canceled, filled, etc)
      removeOrderFromCachedQuery(event.data.orderId);
      return;
    }
    // Active order - update existing, or add new
    addOrUpdateOrderCachedQuery(event.data, true);
  });
};

export const useOrdersQuery = () => {
  const walletAddress = useSelector(state => state.wallet.walletAddress);

  return useQuery({
    queryKey: ['orders', walletAddress],
    queryFn: !walletAddress
      ? skipToken
      : () =>
          fetchAllUserActiveOrders(walletAddress).then(orders =>
            orders.map(order => {
              if (order.conditionalStopLossOrder) {
                order.conditionalStopLossOrder.conditionalOrderId =
                  order.orderId;
              }
              if (order.conditionalTakeProfitOrder) {
                order.conditionalTakeProfitOrder.conditionalOrderId =
                  order.orderId;
              }
              return order;
            })
          ),
    staleTime: minutes(1), // We rely on "useGlobalOrdersQueriesInvalidator"
    refetchOnWindowFocus: false,
    // Warn: This interval is useless when combined with WebSocket setQueryData
    refetchInterval: minutes(1),
  });
};

/** Fetch Query details. Using list */
export const useOrderFromList = (orderId: string) => {
  const { data: orders, isPending, isLoading } = useOrdersQuery();
  const order = orders?.find(order => order.orderId === orderId);
  return {
    data: order as Omit<ApiOrder, 'fills'> | undefined,
    isPending,
    isLoading,
  };
};

const debounceByKey = debouncerByKey();

/** Get active or already executed (settled) order */
export const useOrderQuery = (orderId: string) => {
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const queryClient = useQueryClient();

  useWebsocketEventListener('orders', updatedOrder => {
    if (!updatedOrder.data.type) {
      // Deleverage, Closure, Liquidations..
      return;
    }
    const updatedOrderId = updatedOrder.data.orderId;
    debounceByKey(`${walletAddress}_order:${updatedOrderId}`, 1000, () =>
      queryClient.invalidateQueries({
        queryKey: ['order', walletAddress, updatedOrderId],
      })
    );
  });

  useWebsocketEventListener('webclient', message => {
    const settledEvent = message.data;
    if (settledEvent.event !== 'transaction_settled') {
      return;
    }
    if (settledEvent.action !== 'executeTrade') {
      return;
    }
    // debounceByKey(`${walletAddress}_order:idNotAvailable`, 2000, () =>
    //   queryClient.invalidateQueries({
    //     queryKey: ['order', walletAddress],
    //   })
    // );
    queryClient.setQueryData<ApiOrder[]>(
      ['order', walletAddress, settledEvent.payload.fillId],
      previousOrders => {
        if (!previousOrders) {
          return undefined; // README P9-4765
        }
        const updatedOrders = previousOrders.map(existingOrder =>
          existingOrder.orderId === settledEvent.payload.orderId
            ? {
                ...existingOrder,
                fills: updateMinedOrderFillInArray(
                  existingOrder.fills || [],
                  settledEvent.payload
                ),
              }
            : existingOrder
        );
        return updatedOrders;
      }
    );
  });

  return useQuery({
    queryKey: ['order', walletAddress, orderId],
    queryFn: !walletAddress
      ? skipToken
      : () => fetchOrderDetail(walletAddress, orderId),
    staleTime: seconds(8),
    refetchOnWindowFocus: false,
    refetchInterval: minutes(2),
  });
};
